import { Navigate } from "react-router-dom";
import { FormProvider } from "@/shared/contexts/RegisterContext";
import React from "react";
import EnterpriseList from "@/pages/Enterprise/List";
import FormPage from "@/pages/Enterprise/Form";
import EditEnterprise from "@/pages/Enterprise/Edit";
import UploadEmployeePayroll from "@/pages/Enterprise/UploadEmployeePayroll";
import RateHistory from "@/features/rates/rate-history/pages/RateHistoryPage";
import OperationDetails from "@/features/operations/operation-list/pages/OperationDetailsPage";
import { creditLineClientListService } from "@/features/clients/client-list/services/clientListService";
import { creditLineOperationListService } from "@/features/operations/operation-list/services/operationListService";
import { creditLinePaymentInstallmentsService } from "@/features/operations/payment-installments/services/paymentInstallmentsService";
import { creditLinePaymentListService } from "@/features/operations/payment-list/services/paymentListService";
import { creditLinePaymentRefundsService } from "@/features/operations/payment-refunds/services/paymentRefundsService";
import { getDefaultRouteFromModules } from "@/helpers/navigation_helper";
import { routePaths } from "./appRoutesConfig";

const PageMaintenance = React.lazy(
  () => import("@pages/Utility/PageMaintenance"),
);
const Error404 = React.lazy(() => import("@pages/Utility/Error404"));
const Error500 = React.lazy(() => import("@pages/Utility/Error500"));

const Dashboard = React.lazy(
  () => import("@/features/dashboard/overview/pages/DashboardOverviewPage"),
);
const ProfitLossHistorical = React.lazy(
  () => import("@/features/dashboard/pl/pages/ProfitLossHistoricalPage"),
);
const FxResultDashboard = React.lazy(
  () => import("@/features/dashboard/fx/pages/FxResultDashboardPage"),
);
const AccountingDashboard = React.lazy(
  () => import("@/features/accounting/pages/AccountingDashboardPage"),
);
const AccountingConfiguration = React.lazy(
  () => import("@/features/accounting/pages/AccountingConfigurationPage"),
);
const AccountingExpenses = React.lazy(
  () => import("@/features/accounting/pages/AccountingExpensesPage"),
);
const AccountingFunding = React.lazy(
  () => import("@/features/accounting/pages/AccountingFundingPage"),
);
const AccountingSimulation = React.lazy(
  () => import("@/features/accounting/pages/AccountingSimulationPage"),
);
const RetroactiveInvoicing = React.lazy(
  () =>
    import("@/features/accounting/retroactive-invoicing/pages/RetroactiveInvoicingPage"),
);
const RetroactiveInvoicingDetail = React.lazy(
  () =>
    import("@/features/accounting/retroactive-invoicing/pages/RetroactiveInvoicingDetailPage"),
);
const GenerateInvoice = React.lazy(
  () => import("@/features/invoicing/pages/GenerateInvoicePage"),
);
const NotesPage = React.lazy(
  () => import("@/features/invoicing/pages/NotesPage"),
);
const InvoicingConfiguration = React.lazy(
  () => import("@/features/invoicing/pages/InvoicingConfigurationPage"),
);
const WithholdingsPage = React.lazy(
  () => import("@/features/invoicing/pages/WithholdingsPage"),
);
const PenaltyHistory = React.lazy(
  () => import("@/features/penalties/penalty-history/pages/PenaltyHistoryPage"),
);
const Statistics = React.lazy(() => import("@pages/Statistics/Index"));
const PageLogin = React.lazy(
  () => import("@pages/AuthenticationInner/PageLogin"),
);
const PageRegister = React.lazy(
  () => import("@pages/AuthenticationInner/PageRegister"),
);
const RecoverPassword = React.lazy(
  () => import("@pages/AuthenticationInner/RecoverPassword"),
);
const LockScreen = React.lazy(
  () => import("@pages/AuthenticationInner/LockScreen"),
);
const ConfirmMail = React.lazy(
  () => import("@pages/AuthenticationInner/ConfirmMail"),
);
const EmailVerification = React.lazy(
  () => import("@pages/AuthenticationInner/EmailVerification"),
);
const TwoStepVerfication = React.lazy(
  () => import("@pages/AuthenticationInner/TwoStepVerfication"),
);
const AuthLogout = React.lazy(
  () => import("@pages/AuthenticationInner/Logout"),
);
const Login = React.lazy(() => import("@pages/Authentication/Login"));
const Logout = React.lazy(() => import("@pages/Authentication/Logout"));
const Register = React.lazy(() => import("@pages/Authentication/Register"));
const ForgetPassword = React.lazy(
  () => import("@pages/Authentication/ForgetPassword"),
);
const UserProfile = React.lazy(
  () => import("@pages/Authentication/user-profile"),
);
const PagesComingsoon = React.lazy(
  () => import("@pages/Utility/PageComingsoon"),
);
const ConfigurationsIndex = React.lazy(
  () => import("@/features/configurations/generals/pages/ConfigurationsPage"),
);
const Roles = React.lazy(
  () => import("@/features/configurations/roles/pages/RolesPage"),
);
const CreateRole = React.lazy(
  () => import("@/features/configurations/roles/pages/CreateRolePage"),
);
const EditRole = React.lazy(
  () => import("@/features/configurations/roles/pages/EditRolePage"),
);
const Users = React.lazy(
  () => import("@/features/configurations/users/pages/UsersPage"),
);
const CreateUser = React.lazy(
  () => import("@/features/configurations/users/pages/CreateUserPage"),
);
const EditUser = React.lazy(
  () => import("@/features/configurations/users/pages/EditUserPage"),
);
const ConfirmEmail = React.lazy(
  () => import("@/features/configurations/users/pages/ConfirmEmailPage"),
);
const CreditScore = React.lazy(
  () => import("@/features/configurations/credit-score/pages/CreditScorePage"),
);
const ListClients = React.lazy(
  () => import("@/features/clients/client-list/pages/ClientListPage"),
);
const RegisterLists = React.lazy(
  () => import("@/features/clients/register-list/pages/RegisterListsPage"),
);
const CreateClient = React.lazy(
  () => import("@/features/clients/client-list/pages/CreateClient"),
);
const EditClient = React.lazy(
  () => import("@/features/clients/client-list/pages/EditClient"),
);
const ListDocumentsClient = React.lazy(
  () => import("@/features/clients/client-list/pages/ListDocumentsClient"),
);
const TreasuryIndex = React.lazy(() => import("@pages/Treasury/TreasuryIndex"));
// const NotificationsList = React.lazy(
//   () => import("@pages/Notifications/NotificationsList")
// );
const Operations = React.lazy(
  () => import("@/features/operations/operation-list/pages/OperationListPage"),
);
const Payments = React.lazy(
  () =>
    import("@/features/operations/payment-installments/pages/PaymentInstallmentsPage"),
);
const PaymentsList = React.lazy(
  () => import("@/features/operations/payment-list/pages/PaymentListPage"),
);
const PaymentDetail = React.lazy(
  () => import("@/features/operations/payment-list/pages/PaymentDetailPage"),
);
const PaymentRefunds = React.lazy(
  () =>
    import("@/features/operations/payment-refunds/pages/PaymentRefundListPage"),
);
const PaymentRefundDetail = React.lazy(
  () =>
    import("@/features/operations/payment-refunds/pages/PaymentRefundDetailPage"),
);
const CreditLineList = React.lazy(
  () => import("@/features/credit-line/pages/CreditLineListPage"),
);
const CreditLineCreate = React.lazy(
  () => import("@/features/credit-line/pages/CreditLineCreatePage"),
);
const Balance = React.lazy(() => import("@pages/Balance/Index"));
const GroupsList = React.lazy(
  () => import("@/features/groups/group-list/pages/GroupListPage"),
);
const GroupForm = React.lazy(
  () => import("@/features/groups/group-form/pages/GroupFormPage"),
);
const CampaignList = React.lazy(
  () => import("@/features/notifications/campaigns/pages/CampaignListPage"),
);
const CampaignForm = React.lazy(
  () => import("@/features/notifications/campaigns/pages/CampaignFormPage"),
);
const EmailCampaign = React.lazy(
  () => import("@/features/notifications/emails/pages/EmailCampaignPage"),
);
const DeliveriesList = React.lazy(
  () => import("@/features/notifications/deliveries/pages/DeliveriesListPage"),
);
const MyInbox = React.lazy(
  () => import("@/features/notifications/inbox/pages/MyInboxPage"),
);

export interface RouteProps {
  path: string;
  component: any;
  exact?: boolean;
}

const appTitle = import.meta.env.VITE_APP_TITLE;
const creditLineOperationDetailsPathBase =
  routePaths.creditLine.operations.details.replace("/:id", "");
const creditLinePaymentDetailPathBase =
  routePaths.creditLine.operations.paymentDetail.replace("/:paymentId", "");
const creditLineRefundDetailPathBase =
  routePaths.creditLine.operations.refundDetail.replace("/:refundId", "");

const DefaultAppRedirect = () => (
  <Navigate to={getDefaultRouteFromModules()} replace />
);

const userRoutes: Array<RouteProps> = [
  //dashboard
  {
    path: routePaths.dashboard,
    component: (
      <Dashboard
        title={`Dashboard | ${appTitle}`}
        breadcrumbTitle="Dashboard"
        breadcrumbItem="Dashboard"
      />
    ),
  },
  {
    path: routePaths.statistics.pl,
    component: <ProfitLossHistorical />,
  },
  {
    path: routePaths.fxResults,
    component: <FxResultDashboard />,
  },
  {
    path: routePaths.accounting.dashboard,
    component: <AccountingDashboard />,
  },
  {
    path: routePaths.accounting.configuration,
    component: <AccountingConfiguration />,
  },
  {
    path: routePaths.accounting.expenses,
    component: <AccountingExpenses />,
  },
  {
    path: routePaths.accounting.funding,
    component: <AccountingFunding />,
  },
  {
    path: routePaths.accounting.simulation,
    component: <AccountingSimulation />,
  },
  {
    path: routePaths.invoicing.retroactiveInvoicing,
    component: <RetroactiveInvoicing />,
  },
  {
    path: routePaths.invoicing.retroactiveInvoicingDetail,
    component: <RetroactiveInvoicingDetail />,
  },
  {
    path: routePaths.invoicing.generateInvoice,
    component: <GenerateInvoice />,
  },
  {
    path: routePaths.invoicing.creditNote,
    component: <NotesPage type="NC" />,
  },
  {
    path: routePaths.invoicing.debitNote,
    component: <NotesPage type="ND" />,
  },
  {
    path: routePaths.invoicing.ivaWithholding,
    component: <WithholdingsPage type="RI" />,
  },
  {
    path: routePaths.invoicing.islrWithholding,
    component: <WithholdingsPage type="RR" />,
  },
  {
    path: routePaths.invoicing.configuration,
    component: <InvoicingConfiguration />,
  },
  {
    path: routePaths.penalties.list,
    component: <PenaltyHistory />,
  },
  // {
  //   path: "/statistics",
  //   component: <Statistics />,
  // },

  //profile
  { path: routePaths.profile, component: <UserProfile /> },

  //configurations
  {
    path: routePaths.configurations,
    component: (
      <ConfigurationsIndex
        title={`Configuraciones | ${appTitle}`}
        breadcrumbTitle="Configuraciones"
        breadcrumbItem="Lista de configuraciones"
      />
    ),
  },

  //Roles
  { path: routePaths.roles.list, component: <Roles /> },
  { path: routePaths.roles.create, component: <CreateRole /> },
  { path: routePaths.roles.edit, component: <EditRole /> },

  //Users
  { path: routePaths.users.list, component: <Users /> },
  { path: routePaths.users.create, component: <CreateUser /> },
  { path: routePaths.users.edit, component: <EditUser /> },

  //Credit score
  { path: routePaths.creditScore, component: <CreditScore /> },

  {
    path: routePaths.clients.list,
    component: <ListClients key="clients-list" />,
  },
  {
    path: routePaths.clients.missingPhotos,
    component: <ListClients missingPhotosOnly />,
  },
  { path: routePaths.clients.registerLists, component: <RegisterLists /> },
  {
    path: routePaths.clients.create,
    component: (
      <FormProvider>
        <CreateClient />
      </FormProvider>
    ),
  },
  {
    path: routePaths.clients.edit,
    component: (
      <FormProvider>
        <EditClient />
      </FormProvider>
    ),
  },
  { path: routePaths.clients.documents, component: <ListDocumentsClient /> },

  {
    path: routePaths.treasury,
    component: (
      <TreasuryIndex
        title={`Tesoreria | ${appTitle}`}
        breadcrumbTitle="Tesoreria"
        breadcrumbItem="Tesoreria"
      />
    ),
  },

  // {
  //   path: "/notifications/list",
  //   component: (
  //     <NotificationsList
  //       title={`Notificaciones | ${appTitle}`}
  //       breadcrumbTitle="Notificaciones"
  //       breadcrumbItem="Notificaciones"
  //     />
  //   ),
  // },

  //operations
  {
    path: routePaths.operations.list,
    component: <Operations key="operations-list" />,
  },
  {
    path: routePaths.operations.details,
    component: <OperationDetails />,
  },
  {
    path: routePaths.operations.payments,
    component: <Payments key="operation-installments" />,
  },
  {
    path: routePaths.operations.paymentsList,
    component: <PaymentsList key="operation-payments-list" />,
  },
  {
    path: routePaths.operations.paymentDetail,
    component: <PaymentDetail />,
  },
  {
    path: routePaths.operations.refundsList,
    component: <PaymentRefunds key="operation-refunds-list" />,
  },
  {
    path: routePaths.operations.refundDetail,
    component: <PaymentRefundDetail />,
  },

  //credit line
  {
    path: routePaths.creditLine.create,
    component: <CreditLineCreate />,
  },
  {
    path: routePaths.creditLine.list,
    component: <CreditLineList />,
  },
  {
    path: routePaths.creditLine.clients.list,
    component: (
      <ListClients
        key="credit-line-clients-list"
        service={creditLineClientListService}
        breadcrumbTitle="Línea de Crédito"
        pageTitle="Listado de clientes"
        creditAmountLabels={{
          limit: "Límite de línea",
          available: "Crédito disponible",
          used: "Crédito usado",
        }}
        centerCreditAmountCells
        showActionsOverride={false}
        clientDetailsPathBase="/clients/edit"
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.list,
    component: (
      <Operations
        key="credit-line-operations-list"
        service={creditLineOperationListService}
        breadcrumbTitle="Línea de Crédito"
        breadcrumbItem="Operaciones / Solicitudes de crédito"
        operationDetailsPathBase={creditLineOperationDetailsPathBase}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.details,
    component: (
      <OperationDetails
        endpointBase="/credit-line/operation/get"
        breadcrumbTitle="Línea de Crédito"
        backUrl={routePaths.creditLine.operations.list}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.payments,
    component: (
      <Payments
        key="credit-line-operation-installments"
        service={creditLinePaymentInstallmentsService}
        breadcrumbTitle="Línea de Crédito"
        breadcrumbItem="Operaciones / Cuotas"
        operationDetailsPathBase={creditLineOperationDetailsPathBase}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.paymentsList,
    component: (
      <PaymentsList
        key="credit-line-operation-payments-list"
        service={creditLinePaymentListService}
        breadcrumbTitle="Línea de Crédito"
        breadcrumbItem="Operaciones / Pagos"
        paymentDetailPathBase={creditLinePaymentDetailPathBase}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.paymentDetail,
    component: (
      <PaymentDetail
        endpointBase="/credit-line/operation/payment"
        breadcrumbTitle="Línea de Crédito"
        backUrl={routePaths.creditLine.operations.paymentsList}
        operationDetailsPathBase={creditLineOperationDetailsPathBase}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.refundsList,
    component: (
      <PaymentRefunds
        key="credit-line-operation-refunds-list"
        service={creditLinePaymentRefundsService}
        breadcrumbTitle="Línea de Crédito"
        breadcrumbItem="Operaciones / Reembolsos"
        refundDetailPathBase={creditLineRefundDetailPathBase}
      />
    ),
  },
  {
    path: routePaths.creditLine.operations.refundDetail,
    component: (
      <PaymentRefundDetail
        service={creditLinePaymentRefundsService}
        breadcrumbTitle="Línea de Crédito"
        backUrl={routePaths.creditLine.operations.refundsList}
        paymentDetailPathBase={creditLinePaymentDetailPathBase}
        operationDetailsPathBase={creditLineOperationDetailsPathBase}
      />
    ),
  },

  {
    path: routePaths.balance,
    component: <Balance />,
  },

  //Groups
  { path: routePaths.groups.list, component: <GroupsList /> },
  { path: routePaths.groups.create, component: <GroupForm /> },
  { path: routePaths.groups.edit, component: <GroupForm /> },

  //Notifications V2
  { path: routePaths.notifications.emails, component: <EmailCampaign /> },
  { path: routePaths.notifications.list, component: <CampaignList /> },
  { path: routePaths.notifications.create, component: <CampaignForm /> },
  { path: routePaths.notifications.deliveries, component: <DeliveriesList /> },
  { path: routePaths.notifications.inbox, component: <MyInbox /> },

  {
    path: routePaths.enterprise.list,
    component: <EnterpriseList />,
  },
  {
    path: routePaths.enterprise.create,
    component: <FormPage />,
  },
  {
    path: routePaths.enterprise.edit,
    component: <EditEnterprise />,
  },
  {
    path: routePaths.enterprise.uploadPayroll,
    component: <UploadEmployeePayroll />,
  },
  {
    path: routePaths.rateHistory,
    component: <RateHistory />,
  },
  // this route should be at the end of all other routes
  { path: routePaths.root, exact: true, component: <DefaultAppRedirect /> },
  { path: routePaths.logout, component: <Logout /> },
];

const authRoutes: Array<RouteProps> = [
  //Authentication pages
  { path: routePaths.auth.login, component: <Login /> },
  { path: routePaths.auth.logout, component: <Logout /> },
  { path: routePaths.auth.register, component: <Register /> },
  { path: routePaths.auth.recoverPassword, component: <ForgetPassword /> },

  //AuthenticationInner pages
  { path: routePaths.auth.pageLogin, component: <PageLogin /> },
  { path: routePaths.auth.pageRegister, component: <PageRegister /> },
  { path: routePaths.auth.pageRecoverPassword, component: <RecoverPassword /> },
  { path: routePaths.auth.pageLockScreen, component: <LockScreen /> },
  { path: routePaths.auth.pageConfirmMail, component: <ConfirmMail /> },
  {
    path: routePaths.auth.pageEmailVerification,
    component: <EmailVerification />,
  },
  {
    path: routePaths.auth.pageTwoStepVerification,
    component: <TwoStepVerfication />,
  },
  {
    path: routePaths.auth.pageTwoStepVerification,
    component: <TwoStepVerfication />,
  },
  { path: routePaths.auth.pageLogout, component: <AuthLogout /> },

  //utility page
  { path: routePaths.utility.comingSoon, component: <PagesComingsoon /> },
  { path: routePaths.utility.maintenance, component: <PageMaintenance /> },
  { path: routePaths.utility.notFound, component: <Error404 /> },

  // confirm email
  { path: routePaths.users.confirmEmail, component: <ConfirmEmail /> },
];

const defaultRoutes = [
  { path: routePaths.root, element: <DefaultAppRedirect /> },
  { path: "*", element: <Error404 /> },
];

const serverErrorRoute = {
  path: routePaths.serverError,
  element: <Error500 />,
};

export { userRoutes, authRoutes, defaultRoutes, serverErrorRoute };
